/**
 * components/landing/platform/sections/Layers.jsx — §3.3 Platform Architecture.
 *
 * The 7-layer stack view.  Each layer is clickable and expands to
 * reveal its tech + responsibilities.  Complements (does not repeat)
 * the modular "network" view on the homepage.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import { ChevronDown, Sparkles } from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { LAYERS } from '../data'

const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function Layers() {
  const [openId, setOpenId] = useState(LAYERS[0]?.id)

  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Platform Architecture</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Seven layers, <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">one platform</span></>}
            subtitle="The Kalki Ai Cloud stack is deliberately layered — each layer has a single responsibility, and every module in the platform sits at exactly one layer. This is what lets us ship new capabilities without breaking existing ones."
          />
        </div>

        <div className="flex flex-col gap-3">
          {LAYERS.map((l) => {
            const tone = TONE[l.tone] || TONE.orange
            const open = openId === l.id
            return (
              <div
                key={l.id}
                className={clsx(
                  'relative rounded-2xl border transition-all overflow-hidden',
                  open
                    ? 'border-white/15 bg-gradient-to-b from-white/[0.04] to-white/[0.01]'
                    : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.03]',
                )}
              >
                {open && (
                  <div className="pointer-events-none absolute inset-0 opacity-70"
                    style={{ background: `radial-gradient(60% 40% at 10% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                )}
                <button
                  onClick={() => setOpenId(open ? null : l.id)}
                  aria-expanded={open}
                  className="relative w-full flex items-center gap-4 p-5 md:p-6 text-left"
                >
                  {/* number */}
                  <span
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border font-mono text-sm font-bold"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}
                  >0{l.num}</span>
                  {/* title */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg md:text-xl font-semibold text-white leading-tight">{l.name}</h3>
                    </div>
                    <p className="text-sm text-zinc-400 mt-0.5 truncate">{l.tagline}</p>
                  </div>
                  <ChevronDown className={clsx('h-4 w-4 text-zinc-500 shrink-0 transition-transform', open && 'rotate-180')} />
                </button>

                {open && (
                  <div className="relative grid grid-cols-1 md:grid-cols-5 gap-5 md:gap-8 px-5 md:px-6 pb-6 md:pb-7">
                    <div className="md:col-span-2">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Tech stack</div>
                      <div className="flex flex-wrap gap-1.5">
                        {l.tech.map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-mono text-zinc-300">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="md:col-span-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Responsibilities</div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                        {l.responsibilities.map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: tone.hex }} />
                            <span>{r}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
